import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Upload, File, X, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";

const REQUEST_TYPES = [
  { key: "new_part", label: "تصنيع قطعة جديدة" },
  { key: "reproduce", label: "إعادة إنتاج قطعة موجودة" },
  { key: "repair", label: "إصلاح قطعة" },
  { key: "modify", label: "تعديل قطعة" },
  { key: "emergency", label: "قطعة استبدال طارئة" },
  { key: "reverse_engineer", label: "هندسة عكسية" },
  { key: "from_drawing", label: "تصنيع من رسم فني" },
  { key: "from_sample", label: "تصنيع من عينة مادية" },
];

const URGENCY = [
  { key: "normal", label: "عادي" },
  { key: "urgent", label: "عاجل" },
  { key: "high", label: "حرج (خط إنتاج متوقف)" },
];

const STEPS = ["نوع الطلب", "بيانات القطعة", "رفع الملفات", "الفحص الفني", "المراجعة"];

export default function NewRequest() {
  const [step, setStep] = useState(0);
  const [industries, setIndustries] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState([]);
  const [agree, setAgree] = useState(false);
  const nav = useNavigate();
  const [f, setF] = useState({
    request_type: "new_part",
    part_name: "", part_code: "", machine_name: "", machine_manufacturer: "", machine_model: "",
    industry: "", part_function: "", failure_description: "",
    quantity: 1, material: "", dimensions: "", tolerance: "", surface_finish: "",
    heat_treatment: "", coating: "",
    delivery_date: "", urgency: "normal", annual_demand: 0,
    recurring: false, sample_available: false, machine_stopped: false, notes: "",
    inspection_required: false, inspection_city: "", inspection_area: "", inspection_date: "",
    safety_requirements: "",
  });

  useEffect(() => {
    api.get("/reference/industries").then((r) => setIndustries(r.data.industries || []));
  }, []);

  const upd = (k, v) => setF({ ...f, [k]: v });

  const onFileChange = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    for (const file of list) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "request_attachment");
      try {
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, data]);
      } catch (err) { toast.error(formatApiError(err)); }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const submit = async () => {
    if (!agree) { toast.error("يرجى الموافقة على الإقرار"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...f,
        quantity: Number(f.quantity) || 1,
        annual_demand: Number(f.annual_demand) || 0,
        file_ids: files.map((x) => x.id),
      };
      const { data } = await api.post("/requests", payload);
      toast.success(`تم إرسال الطلب رقم ${data.request_number}`);
      nav("/app/requests");
    } catch (e) { toast.error(formatApiError(e)); }
    setSubmitting(false);
  };

  const canNext = () => {
    if (step === 0) return !!f.request_type;
    if (step === 1) return f.part_name && f.quantity > 0;
    return true;
  };

  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-brand-amber mb-2">طلب جديد</div>
        <h1 className="text-3xl font-bold text-brand-navy">طلب تصنيع قطعة</h1>
        <p className="text-slate-600 mt-1">أكمل الخطوات التالية وسيتولى فريق MERQAV المتابعة معك.</p>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-2 mb-8" data-testid="new-request-steps">
        {STEPS.map((s, i) => {
          let dotClass = "";
          if (i === step) dotClass = "active";
          else if (i < step) dotClass = "done";
          return (
            <div key={s} className="flex-1 flex items-center gap-2">
              <div className={`step-dot ${dotClass}`}>
                {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && <div className={`step-connector ${i < step ? "done" : ""}`} />}
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        {step === 0 && (
          <div>
            <h2 className="text-xl font-bold text-brand-navy mb-4">نوع الطلب</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {REQUEST_TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => upd("request_type", t.key)}
                  className={`card-industrial p-4 text-start ${f.request_type === t.key ? "border-brand-amber ring-2 ring-brand-amber/30" : ""}`}
                  data-testid={`type-${t.key}`}
                >
                  <div className="font-semibold text-brand-navy">{t.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h2 className="text-xl font-bold text-brand-navy mb-4">بيانات القطعة</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>اسم القطعة *</Label><Input required value={f.part_name} onChange={(e) => upd("part_name", e.target.value)} className="mt-1.5" data-testid="req-part-name" /></div>
              <div><Label>الكود الداخلي</Label><Input value={f.part_code} onChange={(e) => upd("part_code", e.target.value)} className="mt-1.5" data-testid="req-part-code" /></div>
              <div><Label>اسم الماكينة</Label><Input value={f.machine_name} onChange={(e) => upd("machine_name", e.target.value)} className="mt-1.5" data-testid="req-machine-name" /></div>
              <div><Label>الشركة المصنعة للماكينة</Label><Input value={f.machine_manufacturer} onChange={(e) => upd("machine_manufacturer", e.target.value)} className="mt-1.5" data-testid="req-machine-manufacturer" /></div>
              <div><Label>موديل الماكينة</Label><Input value={f.machine_model} onChange={(e) => upd("machine_model", e.target.value)} className="mt-1.5" data-testid="req-machine-model" /></div>
              <div>
                <Label>القطاع</Label>
                <Select value={f.industry} onValueChange={(v) => upd("industry", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="req-industry"><SelectValue placeholder="اختر" /></SelectTrigger>
                  <SelectContent>{industries.map((i) => <SelectItem key={i.key} value={i.key}>{i.label_ar}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2"><Label>وظيفة القطعة</Label><Textarea value={f.part_function} onChange={(e) => upd("part_function", e.target.value)} className="mt-1.5" data-testid="req-function" /></div>
              <div className="md:col-span-2"><Label>وصف العطل</Label><Textarea value={f.failure_description} onChange={(e) => upd("failure_description", e.target.value)} className="mt-1.5" data-testid="req-failure" /></div>
              <div><Label>الكمية المطلوبة *</Label><Input required type="number" min={1} value={f.quantity} onChange={(e) => upd("quantity", e.target.value)} className="mt-1.5" data-testid="req-quantity" /></div>
              <div><Label>المادة (إن عُرفت)</Label><Input value={f.material} onChange={(e) => upd("material", e.target.value)} className="mt-1.5" data-testid="req-material" /></div>
              <div><Label>الأبعاد</Label><Input value={f.dimensions} onChange={(e) => upd("dimensions", e.target.value)} placeholder="مثال: 50×30×15 مم" className="mt-1.5" data-testid="req-dimensions" /></div>
              <div><Label>التفاوت المسموح</Label><Input value={f.tolerance} onChange={(e) => upd("tolerance", e.target.value)} placeholder="±0.05 مم" className="mt-1.5" data-testid="req-tolerance" /></div>
              <div><Label>خشونة السطح</Label><Input value={f.surface_finish} onChange={(e) => upd("surface_finish", e.target.value)} className="mt-1.5" data-testid="req-surface" /></div>
              <div><Label>المعالجة الحرارية</Label><Input value={f.heat_treatment} onChange={(e) => upd("heat_treatment", e.target.value)} className="mt-1.5" data-testid="req-heat" /></div>
              <div><Label>الطلاء</Label><Input value={f.coating} onChange={(e) => upd("coating", e.target.value)} className="mt-1.5" data-testid="req-coating" /></div>
              <div><Label>موعد التسليم المطلوب</Label><Input type="date" value={f.delivery_date} onChange={(e) => upd("delivery_date", e.target.value)} className="mt-1.5" data-testid="req-delivery-date" /></div>
              <div>
                <Label>مستوى الاستعجال</Label>
                <Select value={f.urgency} onValueChange={(v) => upd("urgency", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="req-urgency"><SelectValue /></SelectTrigger>
                  <SelectContent>{URGENCY.map((u) => <SelectItem key={u.key} value={u.key}>{u.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>الطلب السنوي المتوقع</Label><Input type="number" value={f.annual_demand} onChange={(e) => upd("annual_demand", e.target.value)} className="mt-1.5" data-testid="req-annual" /></div>
              <div className="flex items-center gap-2 mt-6"><Checkbox checked={f.recurring} onCheckedChange={(v) => upd("recurring", v)} data-testid="req-recurring" /><Label>هل هذه قطعة متكررة؟</Label></div>
              <div className="flex items-center gap-2 mt-6"><Checkbox checked={f.sample_available} onCheckedChange={(v) => upd("sample_available", v)} data-testid="req-sample" /><Label>العينة الأصلية متوفرة</Label></div>
              <div className="flex items-center gap-2 mt-6"><Checkbox checked={f.machine_stopped} onCheckedChange={(v) => upd("machine_stopped", v)} data-testid="req-stopped" /><Label>الماكينة متوقفة حالياً</Label></div>
              <div className="md:col-span-2"><Label>ملاحظات إضافية</Label><Textarea value={f.notes} onChange={(e) => upd("notes", e.target.value)} className="mt-1.5" data-testid="req-notes" /></div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="text-xl font-bold text-brand-navy mb-2">رفع الملفات</h2>
            <p className="text-sm text-slate-600 mb-4">الأنواع المدعومة: صور، فيديو، PDF، Word، Excel، DWG، DXF، STEP، STP، IGES، STL، ZIP</p>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center bg-slate-50 mb-4">
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <div className="text-sm text-slate-600 mb-3">اسحب الملفات هنا أو انقر للاختيار</div>
              <label className="inline-block">
                <input type="file" multiple onChange={onFileChange} className="hidden" data-testid="file-input" />
                <span className="btn-cta px-4 py-2 rounded-md text-sm cursor-pointer inline-flex items-center">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Upload className="w-4 h-4 me-2" />}
                  اختر الملفات
                </span>
              </label>
              <div className="text-xs text-slate-500 mt-3">
                <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-1 rounded"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> ملفات سرية</span>
              </div>
            </div>
            <div className="space-y-2">
              {files.map((f) => (
                <div key={f.id} className="flex items-center justify-between border border-slate-200 rounded-md p-3 bg-white" data-testid={`file-item-${f.id}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <File className="w-4 h-4 text-brand-amber" />
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{f.original_filename}</div>
                      <div className="text-xs text-slate-500">{f.ext?.toUpperCase()} • {(f.size / 1024).toFixed(1)} كب</div>
                    </div>
                  </div>
                  <button onClick={() => removeFile(f.id)} className="text-red-500 hover:text-red-700 p-1" data-testid={`remove-file-${f.id}`}><X className="w-4 h-4" /></button>
                </div>
              ))}
              {files.length === 0 && <div className="text-center text-slate-500 text-sm py-6">لم يتم رفع ملفات بعد.</div>}
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="text-xl font-bold text-brand-navy mb-4">الفحص الفني</h2>
            <div className="flex items-center gap-2 mb-4">
              <Checkbox checked={f.inspection_required} onCheckedChange={(v) => upd("inspection_required", v)} data-testid="req-inspection-required" />
              <Label>يلزم زيارة فنية في الموقع</Label>
            </div>
            {f.inspection_required && (
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>المدينة</Label><Input value={f.inspection_city} onChange={(e) => upd("inspection_city", e.target.value)} className="mt-1.5" data-testid="req-insp-city" /></div>
                <div><Label>المنطقة الصناعية</Label><Input value={f.inspection_area} onChange={(e) => upd("inspection_area", e.target.value)} className="mt-1.5" data-testid="req-insp-area" /></div>
                <div><Label>تاريخ الفحص المفضل</Label><Input type="date" value={f.inspection_date} onChange={(e) => upd("inspection_date", e.target.value)} className="mt-1.5" data-testid="req-insp-date" /></div>
                <div className="md:col-span-2"><Label>متطلبات السلامة وتعليمات الوصول</Label><Textarea value={f.safety_requirements} onChange={(e) => upd("safety_requirements", e.target.value)} className="mt-1.5" data-testid="req-safety" /></div>
              </div>
            )}
            {!f.inspection_required && (
              <div className="bg-slate-50 border border-slate-200 rounded-md p-6 text-center text-slate-600 text-sm">
                لا يلزم فحص فني. سيقوم فريق MERQAV بمراجعة الملفات المرفقة فقط.
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div>
            <h2 className="text-xl font-bold text-brand-navy mb-4">مراجعة نهائية</h2>
            <div className="space-y-4 mb-6">
              <div className="border border-slate-200 rounded-md p-4">
                <div className="font-semibold text-brand-navy mb-2">نوع الطلب</div>
                <div className="text-sm">{REQUEST_TYPES.find((t) => t.key === f.request_type)?.label}</div>
              </div>
              <div className="border border-slate-200 rounded-md p-4">
                <div className="font-semibold text-brand-navy mb-2">بيانات القطعة</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-slate-500">الاسم:</span> {f.part_name}</div>
                  <div><span className="text-slate-500">الكمية:</span> {f.quantity}</div>
                  <div><span className="text-slate-500">المادة:</span> {f.material || "—"}</div>
                  <div><span className="text-slate-500">الأبعاد:</span> {f.dimensions || "—"}</div>
                  <div><span className="text-slate-500">التفاوت:</span> {f.tolerance || "—"}</div>
                  <div><span className="text-slate-500">التسليم:</span> {f.delivery_date || "—"}</div>
                </div>
              </div>
              <div className="border border-slate-200 rounded-md p-4">
                <div className="font-semibold text-brand-navy mb-2">الملفات ({files.length})</div>
                <div className="text-sm text-slate-600">{files.map((x) => x.original_filename).join("، ") || "لا توجد ملفات"}</div>
              </div>
              {f.inspection_required && (
                <div className="border border-slate-200 rounded-md p-4">
                  <div className="font-semibold text-brand-navy mb-2">الفحص الفني</div>
                  <div className="text-sm">{f.inspection_city} — {f.inspection_area} • {f.inspection_date}</div>
                </div>
              )}
            </div>

            <label className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-md p-4 cursor-pointer" data-testid="req-agree-label">
              <Checkbox checked={agree} onCheckedChange={setAgree} data-testid="req-agree-checkbox" />
              <span>أقر بأن المعلومات والملفات المرفوعة صحيحة، وأوافق على أن يقوم فريق MERQAV بدراستها ومشاركتها بشكل سري مع جهات التصنيع المؤهلة.</span>
            </label>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="step-prev-btn">
          <ChevronRight className="w-4 h-4 me-1 rtl-flip" /> السابق
        </Button>
        {step < STEPS.length - 1 ? (
          <Button className="btn-cta" disabled={!canNext()} onClick={() => setStep(step + 1)} data-testid="step-next-btn">
            التالي <ChevronLeft className="w-4 h-4 ms-1 rtl-flip" />
          </Button>
        ) : (
          <Button className="btn-cta" disabled={!agree || submitting} onClick={submit} data-testid="submit-request-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "إرسال الطلب"}
          </Button>
        )}
      </div>
    </DashboardLayout>
  );
}
